import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, OnChanges, OnInit, Input, SimpleChanges, OnDestroy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { IRiskScaleLabel } from '../../interfaces';
import { IDynamicRiskScaleConfig } from '../../interfaces/risk-scale.interface';
import { IBiomarkerValue } from 'src/app/features/test-results/interfaces';
import dsTokens from '@exploris/ce-design-system/tokens.json';

// Risk-scale gradient endpoints, sourced from the design system (single source of
// truth) so a rebrand updates them. VALIDATION-RELEVANT — pair with label + glyph.
const RISK_GREEN = dsTokens.color.riskGradient.low.$value; // #96e061 low likelihood
const RISK_AMBER = dsTokens.color.riskGradient.mid.$value; // #fbd59b intermediate
const RISK_RED = dsTokens.color.riskGradient.high.$value; // #f67e7e high likelihood

@Component({
  standalone: false,
  selector: 'app-risk-scale',
  templateUrl: './risk-scale.component.html',
})
export class RiskScaleComponent implements OnChanges, OnInit, OnDestroy {
  @Input() value = 0; // Value between 0 and 100
  @Input() showTooltip = true;
  @Input() gradient: string | null = null;
  @Input() minimal = false;
  @Input() tooltipText?: string;
  @Input() showRiskCategory = true;
  @Input() biomarkerData?: IBiomarkerValue; // New input for dynamic biomarker data

  position = 0;
  riskCategory = '';
  clampedValue = 0;
  isSmallScreen = false;
  isMediumScreen = false;
  showCustomTooltip = false;
  private destroy$ = new Subject<void>();

  // Normal range properties for biomarker scales
  normalRangeLines: { minPosition: number; maxPosition: number; minLabel: string; maxLabel: string } | null = null;

  // Percentage labels
  percentageLabels: IRiskScaleLabel[] = [
    { text: '0', position: 0, isPercentage: true, isImportant: true },
    { text: '5%', position: 5, isPercentage: true },
    { text: '15%', position: 15, isPercentage: true },
    { text: '50%', position: 50, isPercentage: true, isImportant: true },
    { text: '85%', position: 85, isPercentage: true },
    { text: '100%', position: 100, isPercentage: true, isImportant: true },
  ];

  // Category labels - all 5 categories
  categoryLabels: IRiskScaleLabel[] = [
    { text: 'modules.scores.report.risk_levels.very_low', position: 2.5, isCategory: true },
    { text: 'modules.scores.report.risk_levels.low', position: 10, isCategory: true },
    { text: 'modules.scores.report.risk_levels.moderate', position: 35, isCategory: true },
    { text: 'modules.scores.report.risk_levels.high', position: 70, isCategory: true },
    { text: 'modules.scores.report.risk_levels.very_high', position: 92.5, isCategory: true }, // Added the missing "Very high" category
  ];

  constructor(private breakpointObserver: BreakpointObserver) {}

  ngOnInit(): void {
    // Monitor screen size changes
    this.breakpointObserver
      .observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium, '(max-width: 600px)', '(max-width: 768px)'])
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        this.isSmallScreen = result.breakpoints[Breakpoints.XSmall] || result.breakpoints['(max-width: 600px)'];
        this.isMediumScreen =
          result.breakpoints[Breakpoints.Small] ||
          result.breakpoints[Breakpoints.Medium] ||
          result.breakpoints['(max-width: 768px)'];
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value'] || changes['biomarkerData']) {
      this.updateRiskScale();
    }
  }

  /**
   * @description Updates the risk scale using dynamic biomarker data or fallback to simple value-based positioning
   * @returns {void}
   */
  private updateRiskScale(): void {
    // Handle dynamic biomarker data if available
    if (this.biomarkerData) {
      this.handleDynamicBiomarkerData();
    } else {
      // Simple fallback for non-biomarker usage (like main risk assessment)
      this.clampedValue = Math.min(Math.max(this.value, 0), 100);
      this.position = this.clampedValue;
      this.riskCategory = this.getRiskCategory(this.clampedValue);

      // Use provided gradient or default
      if (!this.gradient) {
        this.gradient = `linear-gradient(90deg, ${RISK_GREEN} 0%, ${RISK_AMBER} 50%, ${RISK_RED} 100%)`;
      }

      // Clear normal range lines for non-biomarker scales
      this.normalRangeLines = null;
    }
  }

  /**
   * @description Handles dynamic biomarker data to calculate position and generate gradient
   * @returns {void}
   */
  private handleDynamicBiomarkerData(): void {
    if (!this.biomarkerData) return;

    const dynamicConfig: IDynamicRiskScaleConfig = {
      measuringMin: this.biomarkerData.measuringMin,
      measuringMax: this.biomarkerData.measuringMax,
      normalMin: this.biomarkerData.normalMin,
      normalMax: this.biomarkerData.normalMax,
      displayValue: this.biomarkerData.displayValue,
      unit: this.biomarkerData.unit,
    };

    // Log warning if data seems invalid but continue processing
    if (!this.isValidBiomarkerData(this.biomarkerData)) {
      console.warn('Biomarker data may be invalid:', dynamicConfig);
    }

    // Calculate position based on displayValue within measuring range
    // Example: displayValue="160", measuringMin=0, measuringMax=200 -> position = 80%
    const numericValue = this.parseDisplayValue(dynamicConfig.displayValue);
    const range = dynamicConfig.measuringMax - dynamicConfig.measuringMin;

    if (range > 0) {
      // Clamp value within measuring range
      const clampedValue = Math.max(dynamicConfig.measuringMin, Math.min(numericValue, dynamicConfig.measuringMax));
      this.position = ((clampedValue - dynamicConfig.measuringMin) / range) * 100;
    } else {
      this.position = 0;
    }

    this.clampedValue = this.position;
    this.riskCategory = this.getRiskCategory(this.position);

    // Generate dynamic gradient based on normal ranges
    this.gradient = this.generateDynamicGradient(dynamicConfig);

    // Generate normal range vertical lines for biomarker scales
    this.generateNormalRangeLines(dynamicConfig);
  }

  /**
   * @description Validates biomarker data to ensure measuring and normal ranges are valid
   * @param {IBiomarkerValue} biomarkerData - The biomarker data to validate
   * @returns {boolean} True if the data is valid, false otherwise
   */
  private isValidBiomarkerData(biomarkerData: IBiomarkerValue): boolean {
    // Check if all required properties exist and are not undefined
    if (
      biomarkerData.measuringMin === undefined ||
      biomarkerData.measuringMax === undefined ||
      biomarkerData.normalMin === undefined ||
      biomarkerData.normalMax === undefined
    ) {
      return false;
    }

    // Check if measuring range is valid (not zero and min < max)
    if (
      (biomarkerData.measuringMin === 0 && biomarkerData.measuringMax === 0) ||
      biomarkerData.measuringMin >= biomarkerData.measuringMax
    ) {
      return false;
    }

    // Check if normal range is valid (not both zero and min <= max)
    if (
      (biomarkerData.normalMin === 0 && biomarkerData.normalMax === 0) ||
      biomarkerData.normalMin > biomarkerData.normalMax
    ) {
      return false;
    }
    return true;
  }

  /**
   * @description Parses display value to get numeric value
   * @param {string} displayValue - The display value to parse
   * @returns {number} The parsed numeric value
   */
  private parseDisplayValue(displayValue: string): number {
    if (!displayValue || displayValue === '-') return 0;
    const numericMatch = displayValue.match(/(\d+\.?\d*)/);
    return numericMatch ? parseFloat(numericMatch[0]) : 0;
  }

  /**
   * @description Generates dynamic gradient based on normal ranges within measuring range
   * @param {IDynamicRiskScaleConfig} config - The dynamic configuration
   * @returns {string} The gradient CSS string
   */
  private generateDynamicGradient(config: IDynamicRiskScaleConfig): string {
    const { measuringMin, measuringMax, normalMin, normalMax } = config;

    const range = measuringMax - measuringMin;
    if (range <= 0) return `linear-gradient(90deg, ${RISK_GREEN} 0%, ${RISK_GREEN} 100%)`;

    // Calculate where normal range sits within the measuring range (as percentages)
    // Example: measuringRange=[0,200], normalRange=[60,80] -> normalStart=30%, normalEnd=40%
    const normalStartPct = Math.max(0, ((normalMin - measuringMin) / range) * 100);
    const normalEndPct = Math.min(100, ((normalMax - measuringMin) / range) * 100);

    // Colors
    const red = RISK_RED;
    const yellow = RISK_AMBER;
    const green = RISK_GREEN;

    // Build gradient stops
    const stops = [];

    // Before normal range (abnormal - red to yellow)
    if (normalStartPct > 0) {
      stops.push(`${red} 0%`);
      if (normalStartPct > 10) {
        stops.push(`${yellow} ${Math.max(0, normalStartPct - 5)}%`);
      }
      stops.push(`${green} ${normalStartPct}%`);
    } else {
      stops.push(`${green} 0%`);
    }

    // Normal range (green)
    if (normalEndPct > normalStartPct) {
      stops.push(`${green} ${normalEndPct}%`);
    }

    // After normal range (abnormal - yellow to red)
    if (normalEndPct < 100) {
      if (normalEndPct < 90) {
        stops.push(`${yellow} ${Math.min(100, normalEndPct + 5)}%`);
      }
      stops.push(`${red} 100%`);
    }

    return `linear-gradient(90deg, ${stops.join(', ')})`;
  }

  /**
   * @description Calculates the risk category based on the value.
   * @param {number} val - The value to calculate the risk category for.
   * @returns {string} The risk category.
   */
  private getRiskCategory(val: number): string {
    if (val < 15) return 'modules.scores.report.risk_levels.very_low';
    if (val < 50) return 'modules.scores.report.risk_levels.low';
    if (val < 85) return 'modules.scores.report.risk_levels.moderate';
    if (val < 95) return 'modules.scores.report.risk_levels.high';
    return 'modules.scores.report.risk_levels.very_high';
  }

  /**
   * @description Gets the visible percentage labels based on screen size.
   * @returns {IRiskScaleLabel[]} The visible percentage labels.
   */
  get visiblePercentageLabels(): IRiskScaleLabel[] {
    if (this.isSmallScreen || this.isMediumScreen) {
      return this.percentageLabels.filter(label => label.isImportant);
    }
    return this.percentageLabels;
  }

  /**
   * @description Gets the visible category labels based on screen size.
   * @returns {IRiskScaleLabel[]} The visible category labels.
   */
  get visibleCategoryLabels(): IRiskScaleLabel[] {
    if (this.isSmallScreen || this.isMediumScreen) {
      return []; // Hide all category labels on small and medium screens
    }
    return this.categoryLabels;
  }

  /**
   * @description Determines if normal range lines should be shown
   * @returns {boolean} True if normal range lines should be displayed
   */
  get shouldShowNormalRangeLines(): boolean {
    return !!(this.normalRangeLines && this.minimal && !this.isSmallScreen);
  }

  /**
   * @description Generates normal range vertical lines and labels for biomarker scales
   * @param {IDynamicRiskScaleConfig} config - The dynamic configuration
   * @returns {void}
   */
  private generateNormalRangeLines(config: IDynamicRiskScaleConfig): void {
    const { measuringMin, measuringMax, normalMin, normalMax } = config;

    // Comprehensive validation
    if (
      measuringMin === undefined ||
      measuringMax === undefined ||
      normalMin === undefined ||
      normalMax === undefined ||
      !isFinite(measuringMin) ||
      !isFinite(measuringMax) ||
      !isFinite(normalMin) ||
      !isFinite(normalMax)
    ) {
      this.normalRangeLines = null;
      return;
    }

    const range = measuringMax - measuringMin;
    if (range <= 0) {
      this.normalRangeLines = null;
      return;
    }

    // Ensure normal range is valid
    if (normalMin > normalMax) {
      this.normalRangeLines = null;
      return;
    }

    // Calculate percentage positions for normal range boundaries
    // Clamp positions to 0-100% range to handle cases where normal range extends outside measuring range
    const minPosition = Math.max(0, Math.min(100, ((normalMin - measuringMin) / range) * 100));
    const maxPosition = Math.max(0, Math.min(100, ((normalMax - measuringMin) / range) * 100));

    // Only show lines if positions are valid and have reasonable separation (at least 3% apart)
    // This prevents overlapping labels and ensures visibility
    if (Math.abs(maxPosition - minPosition) < 3) {
      this.normalRangeLines = null;
      return;
    }

    // Generate labels with raw values only (no units)
    // Up to 3 decimals so limits like 0.019 ng/mL or 2.76 mmol/L are not rounded away; parseFloat drops trailing zeros
    const formatValue = (value: number): string => {
      return Number.parseFloat(value.toFixed(3)).toString();
    };

    // const minLabel = `${formatValue(normalMin)} ${unit || ''}`.trim();
    // const maxLabel = `${formatValue(normalMax)} ${unit || ''}`.trim();
    const minLabel = formatValue(normalMin);
    const maxLabel = formatValue(normalMax);

    this.normalRangeLines = {
      minPosition: Math.round(minPosition * 100) / 100, // Round to 2 decimal places
      maxPosition: Math.round(maxPosition * 100) / 100, // Round to 2 decimal places
      minLabel,
      maxLabel,
    };
  }

  /**
   * @description Gets the appropriate CSS transform for positioning labels based on their position
   * @param {number} position - The position percentage (0-100)
   * @returns {string} The CSS transform string
   */
  getTransformForPosition(position: number): string {
    if (position <= 5) {
      return 'none'; // Left edge - no transform
    } else if (position >= 95) {
      return 'translateX(-100%)'; // Right edge - full left shift
    } else {
      return 'translateX(-50%)'; // Center - half left shift
    }
  }

  /**
   * @description Destroys the component.
   * @returns {void}
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * @description Checks if a value is a valid number
   * @param {any} val - The value to check
   * @returns {boolean} True if the value is a valid number, false otherwise
   */
  isValueNumber(val: number): boolean {
    return typeof val === 'number' && isFinite(val);
  }

  /**
   * @description Calculates the optimal tooltip position to avoid clipping and shaking
   * @returns {object} Object with left, top, and transform CSS values
   */
  getTooltipPosition(): { left: string; top: string; transform: string } {
    // Default positioning above the dot with more space
    let left = this.position + '%';
    let top = '-55px'; // More space above the dot
    let transform = 'translateX(-50%)'; // Center horizontally

    // Adjust for edge positions to prevent clipping
    if (this.position >= 85) {
      // Near right edge - position to the left of the dot
      left = this.position - 2 + '%';
      top = '-35px'; // More space for side positioning too
      transform = 'translateX(-100%)'; // Align to the right edge of tooltip
    } else if (this.position <= 15) {
      // Near left edge - position to the right of the dot
      left = this.position + 2 + '%';
      top = '-35px'; // More space for side positioning too
      transform = 'translateX(0%)'; // Align to the left edge of tooltip
    }

    return { left, top, transform };
  }
}
